import { UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { FormFieldType } from '@vendasta/meetings';
import { DynamicFormControlType, DynamicFormField } from './dynamic-form.interfaces';

export interface FieldMapperOptions {
  /** Pre-resolved options for SELECT fields (differs by caller). Pass [] if no options available. */
  selectOptions: { text: string; value: string }[];
  /** Default scalar value for the control (use '' for no default). */
  defaultValue: string;
  /** Default array value for CHECKBOX_GROUP controls. */
  defaultArrayValue: unknown[];
  /** Additional validators to append (e.g. customEmailValidator for EMAIL fields). */
  extraValidators?: ValidatorFn[];
}

/**
 * Builds a single DynamicFormField for a meeting-type form field given a field type and
 * caller-supplied options. Centralises the DROPDOWN / MULTIPLE_CHOICE / option-mapping logic
 * that is identical across all callers.
 *
 * @param field  A raw form-field object with at minimum `id`, `label`, `required`, `type`,
 *               and optionally `options: string[]`.
 * @param base   Caller-computed base validators (e.g. [required, pattern] when field.required).
 * @param opts   Caller-specific parameterisation (default value, select options, extra validators).
 * @returns      The DynamicFormField, or null when the field type is not handled.
 */
export function buildDynamicFormField(
  field: {
    id: string;
    label: string;
    required: boolean;
    type: FormFieldType;
    options?: string[];
  },
  base: ValidatorFn[],
  opts: FieldMapperOptions
): DynamicFormField | null {
  const allValidators = opts.extraValidators?.length ? [...base, ...opts.extraValidators] : base;

  const commonProps = {
    id: field.id,
    label: field.label,
    required: field.required,
    originalType: field.type,
    formControl: new UntypedFormControl(opts.defaultValue, allValidators.length ? allValidators : null),
  };

  switch (field.type) {
    case FormFieldType.FORM_FIELD_TYPE_TEXT:
      return { ...commonProps, type: DynamicFormControlType.INPUT };

    case FormFieldType.FORM_FIELD_TYPE_EMAIL:
      return { ...commonProps, type: DynamicFormControlType.EMAIL };

    case FormFieldType.FORM_FIELD_TYPE_PHONE_NUMBER:
      return { ...commonProps, type: DynamicFormControlType.PHONE };

    case FormFieldType.FORM_FIELD_TYPE_SELECT:
      return {
        ...commonProps,
        type: DynamicFormControlType.SELECT,
        config: { ...commonProps, options: opts.selectOptions },
      };

    case FormFieldType.FORM_FIELD_TYPE_DROPDOWN:
      return {
        ...commonProps,
        type: DynamicFormControlType.SELECT,
        config: {
          ...commonProps,
          options: (field.options || []).map((option) => ({ text: option, value: option })),
        },
      };

    case FormFieldType.FORM_FIELD_TYPE_MULTIPLE_CHOICE:
      return {
        ...commonProps,
        type: DynamicFormControlType.CHECKBOX_GROUP,
        formControl: new UntypedFormControl(
          opts.defaultArrayValue,
          field.required ? [Validators.required] : null
        ),
        config: {
          ...commonProps,
          options: (field.options || []).map((option) => ({ text: option, value: option })),
        },
      };

    default:
      return null;
  }
}
